"use client";
import { useState, useRef, useEffect } from "react";
import { gsap } from "gsap";
import { useTranslations, useLocale } from "next-intl";
import { ArrowRight, ArrowLeft } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import convertStringToSlug from "@/app/utils/urlFormatter";
import ProjectsSkeleton from "@/app/components/skeleton/ProjectsSkeleton";
import ProjectError from "@/app/components/error/ProjectError";
// api
import ProjectService from '@/app/services/projects';

interface Card {
    title: string;
    subtitle: string;
    description: string;
    bg_image: string;
    image: string;
}

export default function ProjectsCarousel() {
    const t = useTranslations('Projects');
    const locale = useLocale();
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const bgRef = useRef<HTMLDivElement>(null);
    const [data, setData] = useState<any[]>([])
    const [error, setError] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState<boolean>(false)

    async function getProjectsByYear() {
        if (data?.length <= 0) { setIsLoading(true) }
        try {
            const body = {
                year: [2023, 2024, 2025, 2026],
                language: locale,
                type_id:  null,
                no_pagination: 1
            }
            const result: any = await ProjectService.getList(body);
            if (result?.data?.success) {
                setData(result?.data?.data)
                setError(false)
            } else {
                setError(true)
            }
        } catch (error: any) {
            setError(true)
        } finally {
            setIsLoading(false)
        }
    }

    // Animate background change
    useEffect(() => {
        if (!bgRef.current) return;
        gsap.to(bgRef.current, {
            backgroundImage: data?.length > 0 ? `url(${data[activeIndex]?.cover_image[0]?.url})` : '',
            duration: 0.8,
            ease: "power2.out",
        });
    }, [activeIndex]);

    // Optional: GSAP carousel animation
    const carouselRef = useRef<HTMLDivElement>(null);

    const scrollToCard = (index: number) => {
        if (carouselRef.current) {
            const cardWidth = carouselRef.current.children[0].clientWidth + 16; // item width + gap (gap-4 = 16px)
            carouselRef.current.scrollTo({
                left: index * cardWidth,
                behavior: "smooth",
            });
        }
    };

    const prevCard = () => {
        if (activeIndex > 0) {
            setActiveIndex((prev) => {
                const newIndex = prev - 1;
                scrollToCard(newIndex);
                return newIndex;
            });
        }
    };

    const nextCard = () => {
        if (activeIndex < data.length - 1) {
            setActiveIndex((prev) => {
                const newIndex = prev + 1;
                scrollToCard(newIndex);
                return newIndex;
            });
        } else {
            setActiveIndex(0);
            scrollToCard(0)
        }
    };

    useEffect(() => { getProjectsByYear() }, [])
    return (
        <>
            {isLoading ? (
                <>
                    <ProjectsSkeleton />
                </>
            ) : (
                <>
                    {error ? <><ProjectError /></> : (
                        <>
                            {data?.length > 0 ? (
                                <section className="relative w-full h-screen overflow-hidden py-20 px-5 md:px-20">
                                    {/* Background */}
                                    <div
                                        ref={bgRef}
                                        className="absolute inset-0 bg-cover bg-center transition-all duration-700"
                                        style={{ backgroundImage: `url(${data[activeIndex].cover_image[0]?.url})` }}
                                    ></div>

                                    <div className="absolute left-5 md:left-10 top-1/3 md:top-1/2 -translate-y-1/2 z-10 max-w-xs sm:max-w-sm md:max-w-2xl text-white">
                                        <p className="text-sm sm:text-base md:text-2xl">{(data[activeIndex].translations[locale]?.name)}</p>
                                        <h2 className="text-2xl sm:text-3xl md:text-6xl font-medium uppercase my-6">{(data[activeIndex].translations[locale]?.name)}</h2>
                                        <p className="text-sm sm:text-base md:text-md">{(data[activeIndex].translations[locale]?.description)}</p>
                                        <Link href={`/projects/${convertStringToSlug(data[activeIndex]?.translations[locale]?.location)}/${convertStringToSlug(data[activeIndex]?.translations[locale]?.name)}/${data[activeIndex]?.id}`}>
                                            <h3 className='flex mt-6 gap-2 underline text-md md:text-md items-center cursor-pointer'>
                                                {t('learn_more')}
                                                <ArrowRight className="w-6 h-6 rounded-full border-1 px-1 hidden md:block transition-transform group-hover:hidden" color="#ffffff" />
                                            </h3>
                                        </Link>
                                    </div>


                                    {/* Overlay */}
                                    <div className="absolute inset-0 bg-black/40"></div>

                                    {/* Carousel */}
                                    <div className="absolute bottom-5 right-0 md:right-2 transform md:bottom-5 md:translate-x-0 w-full max-w-4xl z-10">
                                        {/* Carousel track */}
                                        <div className="overflow-hidden">
                                            <div
                                                ref={carouselRef}
                                                className="flex gap-4 overflow-x-auto whitespace-nowrap scroll-smooth no-scrollbar"
                                            >
                                                {data.map((card: any, idx) => (
                                                    <div
                                                        key={idx}
                                                        className={`flex-[0_0_300px] w-[20px] h-[240px] md:w-[300px] md:h-[470px] rounded-lg p-1 cursor-pointer transform transition-transform duration-300
                                            ${idx === activeIndex ? "scale-100 shadow-xl" : "scale-95 opacity-70"}`}
                                                        onClick={() => {
                                                            setActiveIndex(idx);
                                                            scrollToCard(idx);
                                                        }}
                                                    >
                                                        <Image
                                                            src={card?.cover_image[0]?.url ?? ''}
                                                            alt={`Projects: ${(data[activeIndex].translations[locale]?.name)}`}
                                                            priority
                                                            fetchPriority="high"
                                                            className="object-cover rounded-lg"
                                                            fill
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Controls */}
                                        <div className="mt-4 flex items-center justify-start gap-0 w-full">
                                            <button onClick={prevCard} aria-label="Previous" className="px-3 py-1" disabled={activeIndex === 0}>
                                                <ArrowLeft className="w-12 h-12 rounded-full border-1 border-white px-3 hidden md:block transition-transform group-hover:hidden" color="#ffffff" />
                                            </button>
                                            <button
                                                onClick={nextCard}
                                                className="px-3 py-1"
                                                aria-label="Next"
                                            //disabled={activeIndex === cards.length - 1}
                                            >
                                                <ArrowRight className="w-12 h-12 rounded-full border-1 border-white px-3 hidden md:block transition-transform group-hover:hidden" color="#ffffff" />
                                            </button>
                                            <div className="flex-1 h-1 bg-white/30 rounded overflow-hidden mx-6">
                                                <div
                                                    className="h-1 bg-white rounded"
                                                    style={{
                                                        width: `${((activeIndex + 1) / data.length) * 100}%`,
                                                        transition: "width 0.3s ease",
                                                    }}
                                                ></div>
                                            </div>
                                            <h2 className="text-2xl md:text-3xl font-medium uppercase w-[45px] rounded-full border-1 border-white text-center py-1 mr-4 color-white">{activeIndex + 1}</h2>
                                        </div>
                                    </div>
                                </section>
                            ) : (
                                <div className="text-center mt-2">
                                    {t('projects_no_data')}
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </>
    );
}
